import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Project {
  _id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  age: string;
  category: string;
  platform: string;
  bannerImage: string;
  isActive: boolean;
  steps: string[];
  content: any;
  requirements: {
    hardware: string[];
    software: string[];
  };
  learning: string[];
  educatorInfo: {
    downloadLink: string;
    printerFriendlyLink: string;
  };
  createdAt: string;
  updatedAt: string;
}

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [currentStep, setCurrentStep] = useState("Introduction");
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch project data from API
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3030'}/highcode/projects/${projectId}`);
        
        if (!response.ok) {
          throw new Error('Project not found');
        }
        
        const data = await response.json();
        setProject(data);
        
        // Set the first step as current if steps exist
        if (data.steps && data.steps.length > 0) {
          setCurrentStep(data.steps[0]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  // Show loading spinner while fetching
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error if project not found
  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-4">{error || 'The project you are looking for does not exist.'}</p>
          <Link 
            to="/all-projects" 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse All Projects
          </Link>
        </div>
      </div>
    );
  }

  // Transform API data to match component expectations
  const projectData = {
    id: project._id,
    title: project.title,
    platform: project.platform,
    level: project.difficulty === 'Easy' ? 1 : project.difficulty === 'Medium' ? 2 : 3,
    description: project.description,
    bannerImage: project.bannerImage,
    steps: project.steps || [],
    content: project.content || {},
    requirements: project.requirements || { hardware: [], software: [] },
    learning: project.learning || [],
    educatorInfo: project.educatorInfo || { downloadLink: "#", printerFriendlyLink: "#" }
  };

  const handleStepClick = (step: string) => {
    setCurrentStep(step);
  };

  const handleNextStep = () => {
    const currentIndex = projectData.steps.indexOf(currentStep);
    if (currentIndex < projectData.steps.length - 1) {
      setCurrentStep(projectData.steps[currentIndex + 1]);
    }
  };

  const handlePreviousStep = () => {
    const currentIndex = projectData.steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(projectData.steps[currentIndex - 1]);
    }
  };

  const getNextStepTitle = () => {
    const currentIndex = projectData.steps.indexOf(currentStep);
    if (currentIndex < projectData.steps.length - 1) {
      return projectData.steps[currentIndex + 1];
    }
    return "Complete";
  };

  const getPreviousStepTitle = () => {
    const currentIndex = projectData.steps.indexOf(currentStep);
    if (currentIndex > 0) {
      return projectData.steps[currentIndex - 1];
    }
    return "Start";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Green Banner Header */}
      <section className="pt-20 px-6 mx-16">
      <div className="bg-green-500 rounded-lg mx-4 mt-4 p-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <span className="bg-black text-white px-3 py-1 rounded text-sm font-medium mr-4">
                ☐☐☐ {projectData.platform} - Level {projectData.level}
              </span>
            </div>
            <h1 className="text-4xl font-bold text-black mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>
              {projectData.title}
            </h1>
          </div>
          <div className="ml-8">
            <div className="bg-blue-100 rounded-lg p-4 w-48 h-32">
              <img 
                src={projectData.bannerImage} 
                alt={projectData.title}
                className="w-full h-full object-contain rounded-md"
              />
            </div>
          </div>
        </div>
      </div>
      </section>

      {/* Main Content Area */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Left Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-bold text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                Project Steps
              </h3>
                <div className="relative">
                  {/* Vertical connecting line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                  
                  {projectData.steps.map((step, index) => (
                    <div key={index} className="relative flex items-start mb-4 last:mb-0">
                      {/* Step circle */}
                      <div className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        currentStep === step 
                          ? 'bg-green-500 border-green-500' 
                          : 'bg-white border-gray-400'
                      }`}>
                        <span className={`text-sm font-bold ${
                          currentStep === step ? 'text-white' : 'text-gray-500'
                        }`}>
                          {index + 1}
                        </span>
                      </div>
                      
                      {/* Step content */}
                      <div className="ml-4 flex-1">
                        <button 
                          onClick={() => handleStepClick(step)}
                          className={`text-left text-lg w-full p-3 rounded-lg transition-all ${
                            currentStep === step 
                              ? 'bg-green-50 text-green-700 font-bold border border-green-200' 
                              : 'text-gray-700 hover:text-green-500 hover:bg-gray-50'
                          }`}
                          style={{fontFamily: 'Lexend, sans-serif'}}
                        >
                          {step}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button className="w-full flex items-center justify-center space-x-2 bg-white border-2 border-black text-black font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors" style={{fontFamily: 'Lexend, sans-serif'}}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </button>
              </div>
            </div>

            {/* User Progress Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <h3 className="text-lg font-bold text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                Save your progress & collect badges!
              </h3>
              <div className="flex space-x-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-4a2 2 0 114 0v4H8z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <button className="w-full bg-white border border-black text-black font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors" style={{fontFamily: 'Lexend, sans-serif'}}>
                Log in or sign up
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-3xl font-bold text-black mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
                {projectData.content[currentStep]?.title || currentStep}
              </h2>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {projectData.content[currentStep]?.description || 'Content not available for this step.'}
                </p>
                
                <h3 className="text-xl font-bold text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {projectData.content[currentStep]?.subheading || 'Instructions'}
                </h3>
                
                <p className="text-lg text-gray-700 mb-8" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {projectData.content[currentStep]?.subdescription || 'Follow the step-by-step instructions to complete this part of the project.'}
                </p>
              </div>

              {/* Scratch Project Canvas */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                  What you will make
                </h3>
                <p className="text-lg text-gray-700 mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                  Click on the green flag, then sit back, relax, and watch the patterns that the butterflies make as they fly.
                </p>
                
                  {/* Scratch Project Frame */}
                  <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden shadow-lg inline-block mx-auto">
                    {/* Scratch Controls */}
                    <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-300">
                      <div className="flex items-center space-x-2">
                        <button className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <div className="text-yellow-600 font-bold text-lg" style={{fontFamily: 'Lexend, sans-serif'}}>
                        SCRATCH
                      </div>
                    </div>
                    
                    {/* Project Stage */}
                    <div className="relative overflow-hidden">
                      <img 
                        src="/kids/what_you_will_learn.png" 
                        alt="Butterfly garden Scratch project"
                        className="w-auto h-auto max-w-full"
                      />
                    </div>
                  </div>
              </div>

              {/* What you will need */}
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-black flex items-center" style={{fontFamily: 'Lexend, sans-serif'}}>
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    What you will need
                  </h3>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2">
                  <div>
                    <strong className="text-black" style={{fontFamily: 'Lexend, sans-serif'}}>Hardware:</strong>
                    <ul className="list-disc list-inside ml-4 text-gray-700" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {projectData.requirements.hardware.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong className="text-black" style={{fontFamily: 'Lexend, sans-serif'}}>Software:</strong>
                    <ul className="list-disc list-inside ml-4 text-gray-700" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {projectData.requirements.software.map((item, index) => (
                        <li key={index}>
                          {item.split('(').map((part, i) => 
                            i === 1 ? (
                              <span key={i}>
                                (<a href="https://scratch.mit.edu/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">online</a> or <a href="https://scratch.mit.edu/download" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">offline</a>)
                              </span>
                            ) : part
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* What you will learn */}
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-black flex items-center" style={{fontFamily: 'Lexend, sans-serif'}}>
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    What you will learn
                  </h3>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                </div>
                <ul className="list-disc list-inside space-y-1 text-gray-700" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {projectData.learning.map((item, index) => (
                    <li key={index}>
                      {item.split('**').map((part, i) => 
                        i % 2 === 1 ? (
                          <span key={i} className="bg-blue-200 px-1 rounded">{part}</span>
                        ) : part
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Additional information for educators */}
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-black flex items-center" style={{fontFamily: 'Lexend, sans-serif'}}>
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Additional information for educators
                  </h3>
                  <button className="text-gray-500 hover:text-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2 text-gray-700" style={{fontFamily: 'Lexend, sans-serif'}}>
                  <p>
                    You can <a href={projectData.educatorInfo.downloadLink} className="text-blue-600 underline">download the completed project here.</a>
                  </p>
                  <p>
                    If you need to print this project, please use the <a href={projectData.educatorInfo.printerFriendlyLink} className="text-blue-600 underline">printer-friendly version.</a>
                  </p>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between">
                <button 
                  onClick={handlePreviousStep}
                  disabled={projectData.steps.indexOf(currentStep) === 0}
                  className={`font-semibold py-3 px-6 rounded-lg transition-colors flex items-center space-x-2 border-2 ${
                    projectData.steps.indexOf(currentStep) === 0
                      ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                      : 'bg-white text-black border-black hover:bg-gray-100'
                  }`}
                  style={{fontFamily: 'Lexend, sans-serif'}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>{getPreviousStepTitle()}</span>
                </button>
                
                <button 
                  onClick={handleNextStep}
                  className="bg-black text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2 border-2 border-purple-600" 
                  style={{fontFamily: 'Lexend, sans-serif'}}
                >
                  <span>{getNextStepTitle()}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
